import { Checkbox, DatePicker, Modal, message } from "antd";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

const REVIEW_LABEL_TO_KEY = { Q1: "q1", "Mid Year": "midyear", Q3: "q3" };
const REVIEW_KEY_TO_LABEL = { q1: "Q1", midyear: "Mid Year", q3: "Q3" };

const emptyReviews = () => ({
  q1: { checked: false, date: null, completed: false },
  midyear: { checked: false, date: null, completed: false },
  q3: { checked: false, date: null, completed: false },
});

const Body = styled.div`
  .lite-text {
    margin-bottom: 8px;
  }
  .review-checkbox-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    .review-checkbox-label {
      width: 140px;
      flex: 0 0 140px;
    }

    .ant-picker {
      width: 180px;
    }
  }
  .footer {
    display: flex;
    justify-content: end;
    gap: 10px;
    margin-top: 24px;
  }
`;

const AdjustDatesModal = ({ open, onClose, record, onSuccess }) => {
  const [startdate, setStartdate] = useState(null);
  const [reviews, setReviews] = useState(emptyReviews());
  const [submitLoading, setSubmitLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    if (!open || !record) return;
    setStartdate(record.startdate ? dayjs(record.startdate) : null);
    const nextReviews = emptyReviews();
    (record.progressreviewdates || []).forEach((item) => {
      const key = REVIEW_LABEL_TO_KEY[item.label];
      if (key) {
        nextReviews[key] = {
          checked: true,
          date: item.date ? dayjs(item.date) : null,
          completed: Boolean(item.completed),
        };
      }
    });
    setReviews(nextReviews);
  }, [open, record]);

  // A checkpoint whose review is already completed is locked here - its
  // checkbox/date can't be reopened or silently changed, since doing so
  // would drop its position in the progress-review cycle.
  const toggleReview = (key, checked) => {
    setReviews((prev) => (prev[key].completed ? prev : { ...prev, [key]: { ...prev[key], checked } }));
  };

  const setReviewDate = (key, date) => {
    setReviews((prev) => (prev[key].completed ? prev : { ...prev, [key]: { ...prev[key], date } }));
  };

  const onAdjust = async () => {
    const missingDate = Object.entries(reviews).find(
      ([, val]) => val.checked && !val.date
    );
    if (missingDate) {
      message.error(`${REVIEW_KEY_TO_LABEL[missingDate[0]]} Review: start date is required`);
      return;
    }

    const progressreviewdates = Object.entries(reviews)
      .filter(([, val]) => val.checked)
      .map(([key, val]) => ({
        label: REVIEW_KEY_TO_LABEL[key],
        date: val.date,
        completed: val.completed,
      }));

    setSubmitLoading(true);
    try {
      const { code } = await CALL_API(
        `performance-kpi-management/adjustdates/${record.id}`,
        "patch",
        { startdate, progressreviewdates }
      );
      if (code === STATUS_CODE.SUCCESS) {
        onClose();
        setPopupOpen(true);
        setTimeout(() => {
          setPopupOpen(false);
          onSuccess?.();
        }, 1500);
      }
    } catch (error) {
      console.error("Error adjusting dates:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <Modal title="Adjust Important Dates" open={open} onCancel={onClose} footer={null}>
        <Body>
          <div className="lite-text">Start date to attend appraisal</div>
          <DatePicker
            value={startdate}
            onChange={setStartdate}
            format="Do MMM YYYY"
            style={{ width: "100%" }}
          />

          <div className="lite-text mt-4 mb-2">
            If you need progress review, select the sessions you want and set a start date for each
          </div>
          <div className="review-checkbox-row">
            <Checkbox
              className="review-checkbox-label"
              checked={reviews.q1.checked}
              disabled={reviews.q1.completed}
              onChange={(e) => toggleReview("q1", e.target.checked)}
            >
              Q1 Review
            </Checkbox>
            <DatePicker
              disabled={!reviews.q1.checked || reviews.q1.completed}
              value={reviews.q1.date}
              onChange={(d) => setReviewDate("q1", d)}
              format="Do MMM YYYY"
            />
            {reviews.q1.completed && <span className="lite-text ms-2">Completed</span>}
          </div>
          <div className="review-checkbox-row">
            <Checkbox
              className="review-checkbox-label"
              checked={reviews.midyear.checked}
              disabled={reviews.midyear.completed}
              onChange={(e) => toggleReview("midyear", e.target.checked)}
            >
              Mid-Year Review
            </Checkbox>
            <DatePicker
              disabled={!reviews.midyear.checked || reviews.midyear.completed}
              value={reviews.midyear.date}
              onChange={(d) => setReviewDate("midyear", d)}
              format="Do MMM YYYY"
            />
            {reviews.midyear.completed && <span className="lite-text ms-2">Completed</span>}
          </div>
          <div className="review-checkbox-row">
            <Checkbox
              className="review-checkbox-label"
              checked={reviews.q3.checked}
              disabled={reviews.q3.completed}
              onChange={(e) => toggleReview("q3", e.target.checked)}
            >
              Q3 Review
            </Checkbox>
            <DatePicker
              disabled={!reviews.q3.checked || reviews.q3.completed}
              value={reviews.q3.date}
              onChange={(d) => setReviewDate("q3", d)}
              format="Do MMM YYYY"
            />
            {reviews.q3.completed && <span className="lite-text ms-2">Completed</span>}
          </div>

          <div className="footer">
            <ButtonOutlined onClick={onClose}>Cancel</ButtonOutlined>
            <ButtonFilled type="primary" loading={submitLoading} onClick={onAdjust}>
              Adjust Date
            </ButtonFilled>
          </div>
        </Body>
      </Modal>

      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type="success"
        message="Date Adjusted"
      />
    </>
  );
};

export default AdjustDatesModal;
